import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Grid, Snackbar, Alert, Box, Button } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import config from '../config';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';

// Import our modern components
import ModernLayout from '../components/layout/ModernLayout';
import OrganizationList from '../components/organizations/OrganizationList';
import OrganizationDetail from '../components/organizations/OrganizationDetail';
import OrganisationsMap from '../components/organizations/OrganisationsMap';
import OrdersTab from '../components/organizations/OrdersTab';
import OrderDetail from '../components/OrderDetail';
import ContactDetailsTab from '../components/organizations/ContactDetailsTab';
import AdvertisingDetailsTab from '../components/organizations/AdvertisingDetailsTab';
import NotesTab from '../components/organizations/NotesTab';
import HistoryTab from '../components/organizations/HistoryTab';
import OrganizationDialog from '../components/organizations/OrganizationDialog';
import { generateSchoolsCsv, downloadCsv } from '../utils/csvExport';
import useOrderSync from '../hooks/useOrderSync';
import testApi from '../utils/testApi';

function ModernOrganizations() {
  const { token, user } = useAuth();
  const { socket } = useSocket();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const autoSelectionHandled = useRef(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  
  // Dialog and form state (simplified for brevity)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [totalCount, setTotalCount] = useState(null);         // real DB total
  const [baseOrganizations, setBaseOrganizations] = useState([]); // initial 1000 loaded records
  const [baseTotalCount, setBaseTotalCount] = useState(null); // total when no search
  const [detailsTabValue, setDetailsTabValue] = useState(0);  const [orderTypeTab, setOrderTypeTab] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailOpen, setOrderDetailOpen] = useState(false);
  
  // Organisation type filter state
  const [organizationTypeFilter, setOrganizationTypeFilter] = useState('school');
  // Ref so async callbacks always see the latest filter without stale closures
  const organizationTypeFilterRef = React.useRef(organizationTypeFilter);
  React.useEffect(() => { organizationTypeFilterRef.current = organizationTypeFilter; }, [organizationTypeFilter]);

  // Additional filter state
  const [regionFilter, setRegionFilter] = useState('all');
  const [postcodeFilter, setPostcodeFilter] = useState('');
  const [consultantFilter, setConsultantFilter] = useState('all');
  
  // Order filtering state
  const [adOrderSearchTerm, setAdOrderSearchTerm] = useState('');
  const [adOrderStatusFilter, setAdOrderStatusFilter] = useState('Invoiced');
  const [schoolOrderSearchTerm, setSchoolOrderSearchTerm] = useState('');
  const [schoolOrderPaymentFilter, setSchoolOrderPaymentFilter] = useState('all');

  // Set up real-time order updates via Socket.IO for organization orders
  useOrderSync({
    orders: selectedOrganization?.orders || [],
    setOrders: (updater) => {
      if (selectedOrganization) {
        setSelectedOrganization(prev => ({
          ...prev,
          orders: typeof updater === 'function' ? updater(prev.orders || []) : updater
        }));
      }
    },
    selectedOrder,
    setSelectedOrder,
    showNotification: true,
    setSnackbar
  });
  
  // Derive available filter options from loaded data
  const availableRegions = [...new Set(
    organizations.map(o => o.region).filter(Boolean)
  )].sort();

  const availableConsultants = [...new Map(
    organizations
      .filter(o => o.sales_consultant_id && o.salesConsultantName)
      .map(o => [o.sales_consultant_id, { id: o.sales_consultant_id, name: o.salesConsultantName }])
  ).values()].sort((a, b) => a.name.localeCompare(b.name));

  // Get filtered organizations — type filter and text search are server-side;
  // only region, postcode and consultant are applied client-side here
  const filteredOrganizations = organizations.filter(org => {
    // Filter by region
    if (regionFilter !== 'all' && org.region !== regionFilter) return false;

    // Filter by postcode (text match)
    if (postcodeFilter) {
      const pc = postcodeFilter.trim();
      if (!org.postcode?.toString().startsWith(pc)) return false;
    }

    // Filter by assigned consultant — schools always show regardless of assignment
    if (consultantFilter !== 'all') {
      const isSchool = org.isSchool || org.is_school;
      if (!isSchool) {
        if (consultantFilter === 'unassigned') {
          if (org.sales_consultant_id) return false;
        } else {
          const orgConsId = org.sales_consultant_id?.toString();
          if (orgConsId !== consultantFilter) return false;
        }
      }
    }

    // Filter by search query — server-side search handles this when query >= 2 chars;
    // keep client-side match for short/empty queries as a fallback
    if (!searchQuery || searchQuery.trim().length < 2) return true;
    const search = searchQuery.toLowerCase();
    return (
      org.name?.toLowerCase().includes(search) ||
      org.suburb?.toLowerCase().includes(search) ||
      org.businessType?.toLowerCase().includes(search) ||
      org.postcode?.toString().includes(search) ||
      org.region?.toLowerCase().includes(search) ||
      org.salesConsultantName?.toLowerCase().includes(search)
    );
  });

  // Get all school organizations
  const schoolOrganizations = organizations.filter(org => 
    org.isSchool || org.is_school
  );

  // Handle export all schools to CSV
  const handleExportAllSchools = () => {
    if (schoolOrganizations.length > 0) {
      const csvContent = generateSchoolsCsv(schoolOrganizations);
      downloadCsv(csvContent);
      
      setSnackbar({
        open: true,
        message: `Exported ${schoolOrganizations.length} school organizations to CSV`,
        severity: 'success'
      });
    } else {
      setSnackbar({
        open: true,
        message: 'No school organizations to export',
        severity: 'info'
      });
    }
  };

  // Filter advertising orders based on search term and status filter
  const filterAdOrders = (orders) => {
    return orders.filter(order => {
      // Filter by order type - check multiple possible values
      const adOrderTypes = ['advertising', 'ad', 'advert', 'advertisement'];
      if (!order.orderType || !adOrderTypes.includes(order.orderType.toLowerCase())) {
        return false;
      }
      
      // Filter by search term
      if (adOrderSearchTerm) {
        const search = adOrderSearchTerm.toLowerCase();
        const matchesSearch = 
          order.businessName?.toLowerCase().includes(search) ||
          order.productType?.toLowerCase().includes(search);
        
        if (!matchesSearch) return false;
      }
      
      // Filter by status
      if (adOrderStatusFilter !== 'all') {
        if (order.status !== adOrderStatusFilter) return false;
      }
      
      return true;
    });
  };
  
  // Filter school orders based on search term and payment filter
  const filterSchoolOrders = (orders) => {
    return orders.filter(order => {
      // Filter by order type
      if (order.orderType !== 'school') return false;
      
      // Exclude cancelled orders
      if (order.status && order.status.toLowerCase() === 'cancelled') return false;
      
      // Filter by search term
      if (schoolOrderSearchTerm) {
        const search = schoolOrderSearchTerm.toLowerCase();
        const matchesSearch = 
          order.schoolName?.toLowerCase().includes(search);
        
        if (!matchesSearch) return false;
      }
      
      // Filter by payment model
      if (schoolOrderPaymentFilter !== 'all') {
        if (order.paymentModel !== schoolOrderPaymentFilter) return false;
      }
      
      return true;
    });
  };

  // Fetch organizations with pagination
  const fetchOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch with pagination - get first 1000 records (should be enough for most cases)
      // Frontend will handle filtering/searching on this dataset
      const response = await fetch(`${config.apiUrl}/api/organizations?limit=1000&page=1`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch organizations: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      
      // Handle both old format (array) and new format (object with data property)
      if (result.success && result.data) {
        const serverTotal = result.pagination?.total ?? result.data.length;
        // Always store as the base set for restoring later
        setBaseOrganizations(result.data);
        setBaseTotalCount(serverTotal);
        // Only apply directly to the visible list if no type/search filter is active —
        // otherwise the unified filter effect is handling the visible list
        if (organizationTypeFilterRef.current === 'all') {
          setOrganizations(result.data);
          setTotalCount(serverTotal);
        }
      } else if (Array.isArray(result)) {
        setOrganizations(result);
        setBaseOrganizations(result);
        setTotalCount(result.length);
        setBaseTotalCount(result.length);
      } else {
        setOrganizations([]);
        setBaseOrganizations([]);
        setTotalCount(0);
        setBaseTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Unified server-side fetch — triggers on search query OR type filter changes.
  // Fetches from the server so results come from ALL records, not just the first 1000.
  React.useEffect(() => {
    const hasSearch = searchQuery && searchQuery.trim().length >= 2;
    const hasTypeFilter = organizationTypeFilter !== 'all';

    if (!hasSearch && !hasTypeFilter) {
      // Nothing active — restore the base 1000 browse records
      if (baseOrganizations.length > 0) {
        setOrganizations(baseOrganizations);
        setTotalCount(baseTotalCount);
      }
      return;
    }

    // Debounce text search by 400ms; type-only filter changes are instant
    const delay = hasSearch ? 400 : 0;

    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({ page: 1 });
        if (hasSearch)     params.set('search', searchQuery.trim());
        if (hasTypeFilter) params.set('type', organizationTypeFilter);
        // Fetch enough to cover all schools (~400) and a sensible slice of businesses
        params.set('limit', hasTypeFilter && !hasSearch ? '5000' : '500');

        const response = await fetch(`${config.apiUrl}/api/organizations?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            setOrganizations(result.data);
            setTotalCount(result.pagination?.total ?? result.data.length);
          }
        }
      } catch (err) {
        console.error('Server-side filter/search error:', err);
      } finally {
        setLoading(false);
      }
    }, delay);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, organizationTypeFilter, token]);

  // Fetch organization details
  const fetchOrganizationDetails = useCallback(async (id) => {
    try {
      if (!token) {
        console.error('No token available for fetching organization details');
        return;
      }
      
      setLoading(true);
      
      // Fetch organization details (which already includes most orders)
      const response = await fetch(`${config.apiUrl}/api/organizations/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch organization details');
      }
      
      const data = await response.json();
      
      // The organization endpoint includes orders, but for school organizations,
      // we need to check if we're missing ad orders due to linking issues
      let orders = data.orders || [];
      
      // Check if this is a school organization and if we have any ad orders
      const isSchoolOrg = data.isSchool === 1 || data.isSchool === true;
      const hasAdOrders = orders.some(order => order.orderType === 'ad');
      
      // If it's a school organization with no ad orders, try fetching ad orders separately
      // This handles cases where ad orders might not be properly linked in the main query
      if (isSchoolOrg && !hasAdOrders) {
        
        try {
          const adOrdersResponse = await fetch(`${config.apiUrl}/api/ad-orders/organization/${id}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (adOrdersResponse.ok) {
            const adOrdersData = await adOrdersResponse.json();
            const additionalAdOrders = adOrdersData.data || [];
            
            if (additionalAdOrders.length > 0) {
              
              // Ensure each ad order has the correct orderType and relationship type.
              // These orders come from /api/ad-orders/organization/:id which only returns
              // orders where this org is the advertiser, so relationshipType is 'business'.
              const processedAdOrders = additionalAdOrders.map(order => ({
                ...order,
                orderType: 'ad',
                relationshipType: 'business'
              }));
              
              // Add only the ad orders that aren't already in the list (prevent duplicates)
              const existingOrderIds = new Set(orders.map(o => o.id));
              const newAdOrders = processedAdOrders.filter(order => !existingOrderIds.has(order.id));
              
              orders = [...orders, ...newAdOrders];
            }
          }
        } catch (adOrderError) {
          console.warn('Failed to fetch additional ad orders for school organization:', adOrderError);
          // Don't fail the whole request if ad orders fetch fails
        }
      }
      
      data.orders = orders;
      
      // Add a type property for easier type checking in components
      if (data.isSchool === 1 || data.isSchool === true) {
        data.type = 'School';
      } else if (data.isBusiness === 1 || data.isBusiness === true) {
        data.type = 'Business';
      } else {
        data.type = 'Other';
      }
      
      setSelectedOrganization(data);
    } catch (error) {
      console.error('Error fetching organization details:', error);
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [token, setSelectedOrganization, setLoading, setSnackbar]);
  
  // Fetch organization history
  const fetchOrganizationHistory = useCallback(async (id) => {
    try {
      setHistoryLoading(true);
      
      const response = await fetch(`${config.apiUrl}/api/organizations/${id}/history`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch organization history');
      }
      
      const data = await response.json();
      
      // Format and process history data
      const processedHistory = Array.isArray(data) ? data.map(entry => ({
        date: entry.timestamp,
        event: entry.field || entry.action || 'Unknown Event',
        oldValue: entry.oldValue || '-',
        newValue: entry.newValue || entry.details || '-',
        user: entry.username || 'System',
        isConsolidated: entry.isConsolidated || entry.action === 'created' || entry.action === 'create'
      })) : [];
      
      // Sort by date, most recent first
      processedHistory.sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setHistoryData(processedHistory);
    } catch (error) {
      console.error('Error fetching organization history:', error);
      setHistoryData([]);
      setSnackbar({
        open: true,
        message: 'Failed to load history data',
        severity: 'error'
      });
    } finally {
      setHistoryLoading(false);
    }
  }, [token, setHistoryData, setHistoryLoading, setSnackbar]);

  // Handle organization click
  const handleOrganizationClick = (organization) => {
    if (organization && organization.id) {
      setAdOrderStatusFilter('Invoiced');
      fetchOrganizationDetails(organization.id);
    } else {
      console.warn('handleOrganizationClick: Invalid organization object', organization);
    }
  };

  // Handle organization add/edit
  const handleOpenDialog = (isEditMode = false, organization = null) => {
    setIsEdit(isEditMode);
    setOpenDialog(true);
  };
  
  // Handle dialog close
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  
  // Handle form submission (add/edit organization)
  const handleSubmitOrganization = async (formData) => {
    try {
      
      const url = isEdit
        ? `${config.apiUrl}/api/organizations/${formData.id}`
        : `${config.apiUrl}/api/organizations`;
      
      const method = isEdit ? 'PUT' : 'POST';
      
      // Prepare request body
      const requestBody = {
        id: formData.id,
        name: formData.name,
        
        // Contact information fields
        primaryName: formData.primary_name,
        primaryEmail: formData.primary_email,
        primaryPhone: formData.primary_phone,
        primaryGeneralEmail: formData.primary_general_email,
        accountsName: formData.accounts_name,
        accountsEmail: formData.accounts_email,
        accountsPhone: formData.accounts_phone,
        accountsGeneralEmail: formData.accounts_general_email,
        principalName: formData.principal_name,
        principalEmail: formData.principal_email,
        principalPhone: formData.principal_phone,
        principalGeneralEmail: formData.principal_general_email,
        businessManagerName: formData.business_manager_name,
        businessManagerEmail: formData.business_manager_email,
        businessManagerPhone: formData.business_manager_phone,
        businessManagerGeneralEmail: formData.business_manager_general_email,
        generalEmail: formData.generalEmail,
        generalPhone: formData.generalPhone,
        website: formData.website,
        
        // Address fields
        address: formData.address,
        state: formData.state,
        suburb: formData.suburb,
        greaterRegion: formData.greaterRegion,
        subRegion: formData.subRegion,
        postcode: formData.postcode,
        postalAddress: formData.postalAddress,
        sameAsStreetAddress: formData.sameAsStreetAddress,
        formattedAddress: formData.formattedAddress,
        latitude: formData.latitude,
        longitude: formData.longitude,
        
        // Status and financial fields
        paymentTerms: parseInt(formData.payment_terms, 10) || 7,
        badDebt: formData.bad_debt,
        businessClosed: formData.business_closed,
        isActive: formData.is_active,
        
        // IMPORTANT: Explicitly set these as boolean values
        isSchool: Boolean(formData.is_school),
        isBusiness: Boolean(formData.is_business),
        
        // Sales consultant assignment
        sales_consultant_id: formData.sales_consultant_id || null,
        
        // School-specific fields
        subscribers: parseInt(formData.subscribers, 10) || 0,
        deployments: parseInt(formData.deployments, 10) || 0,
        numberOfFamilies: parseInt(formData.numberOfFamilies, 10) || 0,
        issuesPerTerm: parseInt(formData.issuesPerTerm, 10) || 0,
        
        // School dropdown fields
        schoolStatus: formData.schoolStatus || null,
        schoolType: formData.schoolType || null,
        schoolYears: formData.schoolYears || null,
        
        // Payment and product fields
        paymentModel: formData.paymentModel,
        adsEnabled: formData.adsEnabled,
        hasSchoolLink: formData.hasSchoolLink,
        hasEPublisher: formData.hasEPublisher,
        hasPaper: formData.hasPaper,
        
        // Product-specific fields
        schoolLinkSubscribers: parseInt(formData.schoolLinkSubscribers, 10) || 0,
        activeSchoolLinkAds: parseInt(formData.activeSchoolLinkAds, 10) || 0,
        deploymentsPerTerm: parseInt(formData.deploymentsPerTerm, 10) || 0,
        ePublisherSubscribers: parseInt(formData.ePublisherSubscribers, 10) || 0,
        ePublisherCost: parseFloat(formData.ePublisherCost) || 0,
        activeEPublisherAds: parseInt(formData.activeEPublisherAds, 10) || 0,
        paperCost: parseFloat(formData.paperCost) || 0,
        paperType: formData.paperType || null,
        activePaperAds: parseInt(formData.activePaperAds, 10) || 0,
        
        // Date fields
        lastInvoiceDate: formData.lastInvoiceDate || null,
        agreementStartDate: formData.agreementStartDate || null,
        agreementEndDate: formData.agreementEndDate || null,
        
        // Other fields
        advertisingObjectives: formData.advertisingObjectives || null,
        businessStatus: formData.businessStatus || null,
        businessType: formData.business_type || null,
        notes: formData.notes,
        additionalContacts: formData.additionalContacts,
      };
      
      // Calculate price based on reach for school organizations
      if (formData.is_school) {
        const deployments = parseInt(formData.deployments, 10) || 0;
        const subscribers = parseInt(formData.subscribers, 10) || 0;
        const reach = (deployments * 4) * subscribers;
        
        // Calculate price based on reach: $900 + (Reach - 5000) x 0.025
        const calculatedPrice = reach > 5000 
          ? 900 + (reach - 5000) * 0.025 
          : 900;
        
        // Add the calculated price to the request body
        requestBody.price = calculatedPrice;
      } else {
        requestBody.price = 0;
      }

      // Always provide a businessType value
      if (formData.is_business) {
        requestBody.businessType = formData.business_type || "Other";
      } else if (formData.is_school) {
        requestBody.businessType = "School";
      } else {
        requestBody.businessType = "N/A";
      }
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || 'Failed to save organization');
      }
      
      // Close dialog and show success message
      handleCloseDialog();
      
      setSnackbar({
        open: true,
        message: isEdit ? 'Organisation updated successfully' : 'Organisation added successfully',
        severity: 'success'
      });
      
      // Refresh organization data
      await fetchOrganizations();
      
      // If we edited the current organization, refresh it
      if (isEdit && selectedOrganization && selectedOrganization.id === formData.id) {
        await fetchOrganizationDetails(formData.id);
      }
      
    } catch (error) {
      console.error('Error submitting organization:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to save organization',
        severity: 'error'
      });
    }
  };

  // Handle new order button click
  const handleNewOrderClick = () => {
    if (!selectedOrganization) return;
    navigate('/app/sales', {
      state: {
        prefillOrg: {
          id:           selectedOrganization.id,
          name:         selectedOrganization.name,
          businessType: selectedOrganization.businessType || '',
          primaryEmail: selectedOrganization.primaryEmail || '',
          primaryPhone: selectedOrganization.primaryPhone || '',
        },
        openCreateOffer: true,
      },
    });
  };

  // Handle order click
  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setOrderDetailOpen(true);
  };

  // Handle order detail close
  const handleOrderDetailClose = () => {
    setOrderDetailOpen(false);
    setSelectedOrder(null);
  };

  // Handle notes update from the notes tab
  const handleNotesUpdate = async (updatedNotes, newNote, options = {}) => {
    const orgId = selectedOrganization?.id?.toString();
    if (orgId) {
      setOrganizations(prev => prev.map(org =>
        org.id.toString() === orgId
          ? {
              ...org,
              notes: org.notes || 'Has notes',
              hasLocalStorageNotes: true
            }
          : org
      ));
    }

    if (!options.localOnly) {
      try {
        await fetchOrganizations();
      } catch (error) {
      }
    }
  };

  // Handle order update
  const handleOrderUpdate = (updatedOrder) => {
    
    // Refresh organization details to get updated orders
    if (selectedOrganization) {
      fetchOrganizationDetails(selectedOrganization.id);
      
      // Show a success notification
      setSnackbar({
        open: true,
        message: 'Order updated successfully',
        severity: 'success'
      });
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setDetailsTabValue(newValue);
    
    // Load history data when switching to the history tab
    // For schools: tab 4 is history (Contact, Advertising, Orders, Notes, History)
    // For businesses: tab 3 is history (Contact, Orders, Notes, History)
    const isSchool = selectedOrganization?.isSchool || selectedOrganization?.is_school;
    const isHistoryTab = isSchool ? newValue === 4 : newValue === 3;
    
    if (isHistoryTab && selectedOrganization) {
      fetchOrganizationHistory(selectedOrganization.id);
    }
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  // Debug function to test API
  const handleDebugClick = async () => {
    await testApi();
    
    // Force refresh the page
    window.location.reload();
  };

  // Define organization event handlers with useCallback
  const handleOrganizationUpdated = useCallback((data) => {
    
    // Update the organization in the list
    setOrganizations(prevOrgs => 
      prevOrgs.map(org => 
        org.id === data.id ? { ...org, ...data.data } : org
      )
    );
    
    // If this is the currently selected organization, update it
    if (selectedOrganization && selectedOrganization.id === data.id) {
      setSelectedOrganization(prev => ({ ...prev, ...data.data }));
      
      // Show a notification
      setSnackbar({
        open: true,
        message: 'Organisation updated',
        severity: 'info'
      });
    }
  }, [selectedOrganization, setOrganizations, setSelectedOrganization, setSnackbar]);
  
  const handleOrganizationCreated = useCallback((data) => {
    
    // Add the new organization to the list
    setOrganizations(prevOrgs => [...prevOrgs, data.data]);
    
    // Show a notification
    setSnackbar({
      open: true,
      message: 'New organization added',
      severity: 'info'
    });
  }, [setOrganizations, setSnackbar]);
  
  const handleOrganizationDeleted = useCallback((data) => {
    
    // Remove the organization from the list
    setOrganizations(prevOrgs => 
      prevOrgs.filter(org => org.id !== data.id)
    );
    
    // If this was the selected organization, clear the selection
    if (selectedOrganization && selectedOrganization.id === data.id) {
      setSelectedOrganization(null);
    }
    
    // Show a notification
    setSnackbar({
      open: true,
      message: 'Organisation deleted',
      severity: 'info'
    });
  }, [selectedOrganization, setOrganizations, setSelectedOrganization, setSnackbar]);

  // Set up socket listeners for organization events
  useEffect(() => {
    if (socket) {
      
      // Register event listeners
      socket.on('organization_updated', handleOrganizationUpdated);
      socket.on('organization_created', handleOrganizationCreated);
      socket.on('organization_deleted', handleOrganizationDeleted);
      
      // Cleanup
      return () => {
        socket.off('organization_updated', handleOrganizationUpdated);
        socket.off('organization_created', handleOrganizationCreated);
        socket.off('organization_deleted', handleOrganizationDeleted);
      };
    }
  }, [socket, handleOrganizationUpdated, handleOrganizationCreated, handleOrganizationDeleted]);
  
  // Define order event handlers with useCallback
  const handleOrderCreated = useCallback((data) => {
    
    if (selectedOrganization && data.organizationId === selectedOrganization.id) {
      fetchOrganizationDetails(selectedOrganization.id);
      
      setSnackbar({
        open: true,
        message: 'New order was created for this organization',
        severity: 'info'
      });
    }
  }, [selectedOrganization, fetchOrganizationDetails, setSnackbar]);
  
  const handleOrderUpdated = useCallback((data) => {
    
    if (selectedOrganization && data.organizationId === selectedOrganization.id) {
      fetchOrganizationDetails(selectedOrganization.id);
      
      setSnackbar({
        open: true,
        message: 'An order was updated for this organization',
        severity: 'info'
      });
    }
  }, [selectedOrganization, fetchOrganizationDetails, setSnackbar]);

  // Handle order events
  useEffect(() => {
    if (socket && selectedOrganization) {
      
      // Register event listeners
      socket.on('order_created', handleOrderCreated);
      socket.on('order_updated', handleOrderUpdated);
      
      // Cleanup
      return () => {
        socket.off('order_created', handleOrderCreated);
        socket.off('order_updated', handleOrderUpdated);
      };
    }
  }, [socket, selectedOrganization, handleOrderCreated, handleOrderUpdated]);

  // Fetch organizations when the component mounts
  useEffect(() => {
    if (token) {
      fetchOrganizations();
    }
  }, [token, fetchOrganizations]);

  // Reset order type tab when organization changes
  useEffect(() => {
    if (selectedOrganization) {
      // Reset to advertising orders tab by default
      setOrderTypeTab(0);
    }
  }, [selectedOrganization?.id]);

  // Load organization details when selected organization changes
  useEffect(() => {
    if (selectedOrganization?.id) {
      fetchOrganizationHistory(selectedOrganization.id);
    }
  }, [selectedOrganization?.id, fetchOrganizationHistory]);

  // Handle navigation state - auto-select organization when returning from orders page
  useEffect(() => {
    if (location.state?.selectedOrganizationId && organizations.length > 0 && !autoSelectionHandled.current) {
      const orgId = parseInt(location.state.selectedOrganizationId);
      const orgToSelect = organizations.find(org => org.id === orgId);
      
      if (orgToSelect) {
        autoSelectionHandled.current = true;
        
        // Use fetchOrganizationDetails to get full organization data with orders
        fetchOrganizationDetails(orgToSelect.id);
        
        // Clear the state immediately to prevent re-triggering
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, organizations, fetchOrganizationDetails]);

  // Reset auto-selection flag when location changes (new navigation)
  useEffect(() => {
    if (!location.state?.selectedOrganizationId) {
      autoSelectionHandled.current = false;
    }
  }, [location.state]);

  return (
    <ModernLayout
      title="Organisations"
      loading={loading}
      actionButton={
        <>
          {organizationTypeFilter === 'school' && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={handleExportAllSchools}
              sx={{ mr: 2 }}
            >
              Export All Schools
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog(false)}
          >
            Add Organisation
          </Button>
        </>
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={4} lg={3}>
          <OrganizationList 
            organizations={filteredOrganizations}
            totalCount={totalCount}
            loading={loading}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedOrganizationId={selectedOrganization?.id}
            onOrganizationClick={handleOrganizationClick}
            typeFilter={organizationTypeFilter}
            onTypeFilterChange={setOrganizationTypeFilter}
            regionFilter={regionFilter}
            onRegionFilterChange={setRegionFilter}
            availableRegions={availableRegions}
            postcodeFilter={postcodeFilter}
            onPostcodeFilterChange={setPostcodeFilter}
            consultantFilter={consultantFilter}
            onConsultantFilterChange={setConsultantFilter}
            availableConsultants={availableConsultants}
          />
        </Grid>
        <Grid item xs={12} md={8} lg={9}>
          {/* Map: always mounted, shown only when no org is selected */}
          <Box sx={{ display: selectedOrganization ? 'none' : 'block' }}>
            <OrganisationsMap
              organisations={filteredOrganizations}
              onSelectOrganisation={handleOrganizationClick}
            />
          </Box>

          {/* Detail panel: only rendered when an org is selected */}
          <OrganizationDetail
            organization={selectedOrganization}
            tabValue={detailsTabValue}
            onTabChange={handleTabChange}
            onEditClick={() => handleOpenDialog(true, selectedOrganization)}
            onNewOrderClick={handleNewOrderClick}
            onShowMap={() => setSelectedOrganization(null)}
          >
            {detailsTabValue === 0 && (
              <ContactDetailsTab 
                organization={selectedOrganization} 
              />
            )}
            {detailsTabValue === 1 && (selectedOrganization?.isSchool || selectedOrganization?.is_school) && (
              <AdvertisingDetailsTab 
                organization={selectedOrganization} 
              />
            )}
            {((detailsTabValue === 1 && !(selectedOrganization?.isSchool || selectedOrganization?.is_school)) || 
              (detailsTabValue === 2 && (selectedOrganization?.isSchool || selectedOrganization?.is_school))) && (
              <OrdersTab 
                organization={selectedOrganization}
                orderTypeTab={orderTypeTab}
                onOrderTypeTabChange={(e, val) => {
                  setOrderTypeTab(val);
                }}
                onOrderClick={handleOrderClick}
                adOrderSearchTerm={adOrderSearchTerm}
                onAdOrderSearchChange={setAdOrderSearchTerm}
                adOrderStatusFilter={adOrderStatusFilter}
                onAdOrderStatusFilterChange={setAdOrderStatusFilter}
                schoolOrderSearchTerm={schoolOrderSearchTerm}
                onSchoolOrderSearchChange={setSchoolOrderSearchTerm}
                schoolOrderPaymentFilter={schoolOrderPaymentFilter}
                onSchoolOrderPaymentFilterChange={setSchoolOrderPaymentFilter}
                filterAdOrders={filterAdOrders}
                filterSchoolOrders={filterSchoolOrders}
                onSchoolOrderCancelled={() => fetchOrganizationDetails(selectedOrganization.id)}
              />
            )}
            {((detailsTabValue === 2 && !(selectedOrganization?.isSchool || selectedOrganization?.is_school)) || 
              (detailsTabValue === 3 && (selectedOrganization?.isSchool || selectedOrganization?.is_school))) && (
              <NotesTab 
                organization={selectedOrganization}
                onNotesUpdate={handleNotesUpdate}
              />
            )}
            {((detailsTabValue === 3 && !(selectedOrganization?.isSchool || selectedOrganization?.is_school)) || 
              (detailsTabValue === 4 && (selectedOrganization?.isSchool || selectedOrganization?.is_school))) && (
              <HistoryTab 
                organization={selectedOrganization}
                historyData={historyData}
                loading={historyLoading}
              />
            )}
          </OrganizationDetail>
        </Grid>
      </Grid>
      
      <OrganizationDialog 
        open={openDialog}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitOrganization}
        isEdit={isEdit}
        organization={isEdit ? selectedOrganization : null}
        token={token}
      />
      
      <OrderDetail
        open={orderDetailOpen}
        order={selectedOrder}
        onClose={handleOrderDetailClose}
        onUpdate={handleOrderUpdate}
      />
      
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ModernLayout>
  );
}

export default ModernOrganizations; 